import { Node, type JSONContent } from "@tiptap/core";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";

interface MdNode {
  type: string; children?: MdNode[]; value?: string; depth?: number;
  ordered?: boolean; start?: number; checked?: boolean | null; lang?: string;
  url?: string; title?: string | null; alt?: string; identifier?: string;
  align?: Array<"left" | "right" | "center" | null>;
  position?: { start: { offset?: number }; end: { offset?: number } };
}
export const safeMarkdownUrl = (url: string) => /^(https?:\/\/|mailto:|#)/i.test(url) ? url : "";

/** Preserve image syntax as an inert atom: no DOM img, remote fetch or active HTML. */
export const MarkdownImageNote = Node.create({
  name: "markdownImage", group: "inline", inline: true, atom: true,
  addAttributes: () => ({ markdown: { default: "" }, alt: { default: "" } }),
  parseHTML: () => [],
  renderHTML: ({ node }) => ["span", { class: "markdown-image-note", contenteditable: "false" }, "[图片：" + (node.attrs.alt || "未命名") + "]"],
  renderMarkdown: (node) => String(node.attrs?.markdown ?? ""),
});

const processor = unified().use(remarkParse).use(remarkGfm);
/** AST -> schema nodes, with no HTML parser or DOM insertion of user strings. */
export function markdownDocument(source: string): JSONContent {
  const root = processor.parse(source) as MdNode;
  const definitions = new Map<string, MdNode>();
  function collect(node: MdNode) {
    if (node.type === "definition") definitions.set(node.identifier ?? "", node);
    node.children?.forEach(collect);
  }
  collect(root);
  function children(node: MdNode): JSONContent[] { return (node.children ?? []).flatMap(convert); }
  function convert(node: MdNode): JSONContent[] {
    const content = () => children(node);
    const block = (type: string, attrs?: Record<string, unknown>): JSONContent[] => [{ type, ...(attrs ? { attrs } : {}), content: content() }];
    switch (node.type) {
      case "root": return block("doc");
      case "text": return node.value ? [{ type: "text", text: node.value }] : [];
      case "paragraph": return block("paragraph");
      case "heading": return block("heading", { level: node.depth ?? 1 });
      case "blockquote": return block("blockquote");
      case "thematicBreak": return [{ type: "horizontalRule" }];
      case "break": return [{ type: "hardBreak" }];
      case "inlineCode": return [{ type: "text", text: node.value ?? "", marks: [{ type: "code" }] }];
      case "code": return [{ type: "codeBlock", attrs: { language: node.lang ?? null }, content: node.value ? [{ type: "text", text: node.value }] : [] }];
      case "strong": case "emphasis": case "delete": {
        const mark = { type: { strong: "bold", emphasis: "italic", delete: "strike" }[node.type] };
        return content().map((item) => ({ ...item, marks: [...(item.marks ?? []), mark] }));
      }
      case "link": case "linkReference": {
        const target = node.type === "linkReference" ? definitions.get(node.identifier ?? "") : node;
        const href = safeMarkdownUrl(target?.url ?? "");
        return href ? content().map((item) => ({ ...item, marks: [...(item.marks ?? []), { type: "link", attrs: { href, title: target?.title ?? null, target: "_blank", rel: "noopener noreferrer" } }] })) : content();
      }
      case "image": case "imageReference": {
        const from = node.position?.start.offset;
        const to = node.position?.end.offset;
        const target = node.type === "imageReference" ? definitions.get(node.identifier ?? "") : node;
        // References are expanded so serialization does not lose their definition.
        const markdown = node.type === "imageReference" ? "![" + (node.alt ?? "") + "](<" + (target?.url ?? "") + ">)"
          : from !== undefined && to !== undefined ? source.slice(from, to) : "";
        return [{ type: "markdownImage", attrs: { markdown, alt: node.alt ?? "" } }];
      }
      case "list": {
        // Split mixed task/bullet lists into valid consecutive schema lists.
        const groups: JSONContent[] = [];
        for (const child of node.children ?? []) {
          const task = typeof child.checked === "boolean";
          const type = task ? "taskList" : node.ordered ? "orderedList" : "bulletList";
          let group = groups.at(-1);
          if (group?.type !== type) { group = { type, attrs: node.ordered ? { start: node.start ?? 1 } : undefined, content: [] }; groups.push(group); }
          group.content!.push({ type: task ? "taskItem" : "listItem", ...(task ? { attrs: { checked: child.checked } } : {}), content: children(child) });
        }
        return groups;
      }
      case "table": return [{ type: "table", content: (node.children ?? []).map((row, index) => ({
        type: "tableRow", content: (row.children ?? []).map((cell, column) => ({
          type: index === 0 ? "tableHeader" : "tableCell", attrs: { align: node.align?.[column] ?? null },
          content: [{ type: "paragraph", content: children(cell) }],
        })),
      })) }];
      case "definition": return [];
      // Unsupported raw HTML stays inert literal text; never becomes live DOM.
      case "html": return node.value ? [{ type: "text", text: node.value }] : [];
      default: return node.children ? content() : node.value ? [{ type: "text", text: node.value }] : [];
    }
  }
  const result = convert(root)[0] ?? { type: "doc", content: [] };
  // HTML at block level is still plain text, wrapped in an editable paragraph.
  result.content = (result.content ?? []).map((node) => node.type === "text" ? { type: "paragraph", content: [node] } : node);
  if (!result.content.length) result.content = [{ type: "paragraph" }];
  return result;
}
